import { Component, Input } from '@angular/core';
import { DatSegmentDetail, StatusColor } from '../../models/monitoring.models';
import { StatusPillComponent } from './ui-shell.component';

@Component({
  selector: 'app-dat-detail-card',
  standalone: true,
  imports: [StatusPillComponent],
  template: `
    @if (detail) {
      <article class="detail-card">
        <h2 class="card-title">{{ detail.datName }} ({{ detail.stationName }})</h2>
        <!-- emplacement de la machine dans la gare. La carte a la place
             d'afficher le libellé long, contrairement à la liste des DAT. -->
        @if (detail.location || detail.usageName) {
          <p class="card-location">{{ detail.location || detail.usageName }}</p>
        }
        <p class="card-status">
          Etat <app-status-pill [color]="detail.color" />
          de {{ detail.startLabel }} à {{ detail.endLabel }}
        </p>
        <p class="card-date">le {{ detail.date }}</p>
 
        @if (detail.color === 'grey') {
          <p class="card-no-data">
            Aucune information remontée sur ce créneau
            @if (detail.noDataSinceLabel) {
              <!-- KUBA-35289 : la date était calculée par le back mais jamais rendue ici,
                   alors que le bloc « Dernière remontée connue » juste en dessous la lit
                   déjà. Elle est désormais toujours affichée : le back l'envoie pour tous
                   les créneaux, et sinceDate() retombe sur la date du créneau consulté
                   pour une réponse plus ancienne, qui ne la portait que lorsqu'elle
                   différait de ce jour-là. -->
              <span class="no-data-since">
                depuis le {{ sinceDate(detail.noDataSinceDate) }} à
                {{ detail.noDataSinceLabel }}
              </span>
            }
          </p>
        }
        @if (detail.greySinceLabel) {
          <p class="card-grey-since">
            <span class="grey-since-text">
              Dernière remontée connue le {{ sinceDate(detail.greySinceDate) }}
              à {{ detail.greySinceLabel }}
            </span>
            <!-- Décision du 11/09 : sans la couleur d'alors, « plus de nouvelles depuis
                 13h32 » ne dit pas si la machine tournait ou était en panne au moment où
                 elle s'est tue — or c'est ce qui décide de l'action de l'agent. -->
            @if (detail.greySinceColor) {
              <span class="grey-since-state">
                <app-status-pill [color]="detail.greySinceColor" />
                <span class="grey-since-word">{{ stateWord(detail.greySinceColor) }}</span>
              </span>
            }
          </p>
        }
        <!-- Formats attendus (spéc. 30/06) :
             « Imprimante reçu : HS (KO : 10h32) » — toujours KO à la fin du créneau ;
             « TPE : OK (KO : 10h25, OK : 10h34) » — repassé OK dans le même créneau. -->
        <ul class="organs">
          @for (organ of detail.organs; track organ.name) {
            <li class="organ-row">
              <span class="organ-name">{{ organ.name }}</span>
              <span class="organ-state">
                <span
                  class="status-badge"
                  [class.ok]="organ.status === 'OK'"
                  [class.hs]="organ.status === 'HS'"
                >
                  {{ organ.status }}
                </span>
                @if (organ.koLabel) {
                  <span class="event-chip ko" [title]="'Passage HS à ' + organ.koLabel">
                    <span class="chip-dot" aria-hidden="true"></span>KO {{ organ.koLabel }}
                  </span>
                }
                @if (organ.okLabel) {
                  <span class="chip-arrow" aria-hidden="true">→</span>
                  <span class="event-chip back-ok" [title]="'Retour OK à ' + organ.okLabel">
                    <span class="chip-dot" aria-hidden="true"></span>OK {{ organ.okLabel }}
                  </span>
                }
              </span>
            </li>
          }
        </ul>
      </article>
    }
  `,
  styles: [
    `
      .detail-card {
        border: 1px solid #555;
        border-radius: 6px;
        padding: 1.25rem 1.5rem;
        background: #fff;
        max-width: 22rem;
        margin: 0 auto;
      }

      .card-title {
        text-align: center;
        margin: 0 0 0.75rem;
        font-size: 1.05rem;
        font-weight: 600;
      }

      .card-status {
        text-align: center;
        margin: 0 0 0.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
        flex-wrap: wrap;
      }

      .card-date {
        text-align: center;
        margin: 0 0 0.5rem;
        color: #444;
      }

      .card-location {
        text-align: center;
        margin: -0.5rem 0 0.75rem;
        color: #555;
        font-size: 0.9rem;
      }

      .no-data-since {
        display: block;
        font-weight: 700;
        color: #333;
        margin-top: 0.15rem;
      }

      .card-no-data {
        text-align: center;
        margin: 0.5rem 0 0.35rem;
        padding: 0.5rem 0.6rem;
        border-radius: 6px;
        background: #f5f5f5;
        border: 1px solid #ddd;
        color: #444;
        font-size: 0.9rem;
      }

      .card-grey-since {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.4rem;
        text-align: center;
        margin: 0 0 1rem;
        padding: 0.5rem 0.6rem;
        border-radius: 6px;
        background: #f0f0f0;
        color: #555;
        font-size: 0.85rem;
      }

      .grey-since-state {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
      }

      .grey-since-word {
        font-weight: 700;
        color: #333;
      }

      .organs {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
      }

      .organ-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.45rem 0.25rem;
        flex-wrap: wrap;
      }

      .organ-row + .organ-row {
        border-top: 1px solid #eee;
      }

      .organ-name {
        font-size: 0.95rem;
      }

      .organ-state {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        flex-wrap: wrap;
      }

      .status-badge {
        display: inline-block;
        min-width: 2.1rem;
        text-align: center;
        padding: 0.1rem 0.5rem;
        border-radius: 999px;
        font-size: 0.8rem;
        font-weight: 700;
      }

      .status-badge.ok {
        color: #2e7d32;
        background: #e8f5e9;
      }

      .status-badge.hs {
        color: #c62828;
        background: #fdecea;
      }

      .event-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.1rem 0.5rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 600;
        white-space: nowrap;
      }

      .event-chip.ko {
        color: #b71c1c;
        background: #fff0ef;
        border: 1px solid #f5c6c4;
      }

      .event-chip.back-ok {
        color: #1b5e20;
        background: #f0f8f1;
        border: 1px solid #c3e0c6;
      }

      .chip-dot {
        width: 0.4rem;
        height: 0.4rem;
        border-radius: 50%;
        background: currentColor;
      }

      .chip-arrow {
        color: #999;
        font-size: 0.75rem;
      }
    `,
  ],
})
export class DatDetailCardComponent {
  @Input() detail: DatSegmentDetail | null = null;

  /**
   * Date à afficher à côté d'une heure de silence. Le back la renvoie désormais pour tous
   * les créneaux ; il ne l'envoyait auparavant que lorsqu'elle tombait un autre jour que le
   * créneau consulté, d'où les deux libellés vus en recette pour une même remontée
   * (« depuis 16h42 » puis « depuis le 14/09/2026 à 16h42 »). Absente — réponse d'un back
   * pas encore déployé, ou cache navigateur — on retombe sur la date du créneau : sous
   * l'ancien contrat, son omission signifiait précisément « même jour que le créneau ».
   */
  sinceDate(date?: string | null): string {
    return date ?? this.detail?.date ?? '';
  }

  /**
   * Mot qui accompagne la pastille de la dernière remontée connue. La couleur seule ne
   * suffit pas : elle disparaît sous le mode sombre agressif de certains navigateurs
   * (KUBA-34835) et n'est pas lisible par tous. Le mot, lui, survit toujours.
   */
  stateWord(color: StatusColor): string {
    switch (color) {
      case 'green':
        return 'OK';
      case 'red':
        return 'Hors service';
      case 'orange':
      case 'yellow':
        return 'Partiel';
      default:
        return '';
    }
  }
}
